import {
  FLEET_DAMAGE_PHOTO_MAX,
  FleetOperationalStatus,
  PermissionCode,
} from '@gb-ticket/shared-types';
import { type ChangeEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import {
  FLEET_DAMAGE_EVENT_LABELS,
  FLEET_DAMAGE_KIND_LABELS,
  FLEET_DAMAGE_SEVERITY_LABELS,
  FLEET_DAMAGE_STATUS_LABELS,
  formatDeDateTime,
} from '@/products/fleet/features/fleet-labels';
import {
  fleetPanelClassName,
  fleetTableFrameClassName,
} from '@/products/fleet/features/fleet-panel';
import { FleetDocumentDeleteButton } from '@/products/fleet/features/fleet-document-delete-button';
import { FleetStatusBadge } from '@/products/fleet/features/fleet-status-badge';
import {
  fleetDocumentDownloadHref,
  useCreateFleetDamage,
  useFleetDamage,
  useFleetVehicles,
} from '@/products/fleet/features/use-fleet';
import { FLEET_REPORT_PATH } from '@/products/fleet/fleet-paths';

type Step = 1 | 2 | 3;

export function FleetReportPage() {
  usePageTitle('Meldung');
  const copy = useVoiceCopy();
  const me = useMe();
  const manage = hasPermission(me.data, PermissionCode.FLEET_VEHICLES_MANAGE);
  const [search] = useSearchParams();
  const fromQuery = search.get('meldung');
  const vehicles = useFleetVehicles(
    false,
    manage ? undefined : { mine: true, operationalStatus: FleetOperationalStatus.IN_SERVICE },
  );
  const create = useCreateFleetDamage();
  const [step, setStep] = useState<Step>(1);
  const [vehicleId, setVehicleId] = useState('');
  const [kind, setKind] = useState<'damage' | 'defect' | ''>('');
  const [severity, setSeverity] = useState<'drivable' | 'not_drivable' | ''>('');
  const [description, setDescription] = useState('');
  const [locationNote, setLocationNote] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submittedId, setSubmittedId] = useState<string | null>(fromQuery);
  const submitted = useFleetDamage(submittedId ?? '');

  const ownVehicles = (vehicles.data?.items ?? []).filter((row) => {
    if (row.operationalStatus === FleetOperationalStatus.RETIRED) {
      return false;
    }
    if (!manage && row.operationalStatus !== FleetOperationalStatus.IN_SERVICE) {
      return false;
    }
    return true;
  });
  const selected = ownVehicles.find((row) => row.id === vehicleId);

  function onPhotosChange(event: ChangeEvent<HTMLInputElement>) {
    const next = [...(event.target.files ?? [])].slice(0, FLEET_DAMAGE_PHOTO_MAX);
    setPhotos(next);
    setPreviews(next.map((file) => URL.createObjectURL(file)));
  }

  async function onSubmit() {
    setError(null);
    if (!vehicleId || !kind || !severity || !description.trim()) {
      setError('Bitte alle Pflichtfelder ausfüllen.');
      return;
    }
    try {
      const result = await create.mutateAsync({
        vehicleId,
        kind,
        severity,
        description: description.trim(),
        locationNote: locationNote.trim() || undefined,
        photos,
      });
      setSubmittedId(result.id);
      toast.success('Meldung gesendet.');
    } catch (err) {
      const message =
        err instanceof ApiRequestError ? err.message : 'Die Meldung konnte nicht gesendet werden.';
      setError(message);
      toast.error(message);
    }
  }

  if (submittedId) {
    return (
      <div className="mx-auto max-w-lg space-y-6 pb-24 lg:pb-6">
        <PlatformPageHeader title="Meldung gesendet" />
        {submitted.isPending ? (
          <p className="text-sm text-muted-foreground">Wird geladen …</p>
        ) : null}
        {submitted.data ? (
          <div className={`${fleetPanelClassName} space-y-4`}>
            <p className="text-3xl font-semibold tracking-tight">
              {submitted.data.licensePlate ?? 'Fahrzeug'}
            </p>
            <p className="text-lg font-medium">
              Status: {FLEET_DAMAGE_STATUS_LABELS[submitted.data.status]}
            </p>
            <p className="text-sm text-muted-foreground">
              {FLEET_DAMAGE_KIND_LABELS[submitted.data.kind]} ·{' '}
              {FLEET_DAMAGE_SEVERITY_LABELS[submitted.data.severity]}
            </p>
            <p className="text-sm">{submitted.data.description}</p>
            {submitted.data.photos && submitted.data.photos.length > 0 ? (
              <section className="space-y-2">
                <h2 className="text-base font-semibold">
                  {submitted.data.photos.length === 1
                    ? '1 Foto'
                    : `${submitted.data.photos.length} Fotos`}
                </h2>
                <ul className={`${fleetTableFrameClassName} divide-y text-sm`}>
                  {submitted.data.photos.map((photo) => (
                    <li
                      key={photo.id}
                      className="flex flex-wrap items-center justify-between gap-2 p-3"
                    >
                      <span className="font-medium">{photo.fileName}</span>
                      <span className="flex flex-wrap items-center gap-3">
                        <a
                          href={fleetDocumentDownloadHref(photo.id)}
                          className="font-medium text-primary underline-offset-4 hover:underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Herunterladen
                        </a>
                        <FleetDocumentDeleteButton
                          documentId={photo.id}
                          fileName={photo.fileName}
                          subject="an dieser Meldung"
                        />
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            <section className="space-y-2">
              <h2 className="text-base font-semibold">Verlauf</h2>
              <ul className={`${fleetTableFrameClassName} divide-y text-sm`}>
                {(submitted.data.events ?? []).map((event) => (
                  <li key={event.id} className="space-y-1 p-3">
                    <p className="font-medium">{FLEET_DAMAGE_EVENT_LABELS[event.type]}</p>
                    <p className="text-muted-foreground">
                      {formatDeDateTime(event.createdAt)}
                      {event.actorName ? ` · ${event.actorName}` : null}
                    </p>
                    {event.note ? <p>{event.note}</p> : null}
                  </li>
                ))}
              </ul>
            </section>
            <Button asChild variant="outline">
              <Link to={FLEET_REPORT_PATH}>Neue Meldung</Link>
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg space-y-6 pb-36 lg:pb-6">
      <PlatformPageHeader title="Meldung" description={`Schritt ${step} von 3`} />

      {vehicles.isPending ? (
        <p className="text-sm text-muted-foreground">Fahrzeuge werden geladen …</p>
      ) : null}

      {ownVehicles.length === 0 && !vehicles.isPending ? (
        <p className="text-sm text-muted-foreground">
          {manage
            ? 'Kein Fahrzeug für eine Meldung. Stillgelegte Fahrzeuge sind ausgenommen.'
            : copy('fleet.vehicle.none-today')}
        </p>
      ) : null}

      {step === 1 && ownVehicles.length > 0 ? (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Fahrzeug wählen</p>
          <ul className="space-y-2">
            {ownVehicles.map((vehicle) => (
              <li key={vehicle.id}>
                <button
                  type="button"
                  onClick={() => setVehicleId(vehicle.id)}
                  className={cn(
                    'w-full rounded-xl border border-border bg-card px-4 py-4 text-left shadow-sm',
                    vehicleId === vehicle.id ? 'border-primary bg-primary/5' : 'hover:bg-muted/40',
                  )}
                >
                  <span className="block text-3xl font-semibold tracking-tight sm:text-4xl">
                    {vehicle.licensePlate}
                  </span>
                  <span className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                    <span>
                      {vehicle.make} {vehicle.model}
                    </span>
                    {vehicle.operationalStatus !== FleetOperationalStatus.IN_SERVICE ? (
                      <FleetStatusBadge status={vehicle.operationalStatus} />
                    ) : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {step === 2 && selected ? (
        <div className={`${fleetPanelClassName} space-y-5`}>
          <p className="text-3xl font-semibold tracking-tight sm:text-4xl">
            {selected.licensePlate}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <Choice active={kind === 'damage'} label="Schaden" onClick={() => setKind('damage')} />
            <Choice active={kind === 'defect'} label="Mangel" onClick={() => setKind('defect')} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Choice
              active={severity === 'drivable'}
              label="Fahrbereit"
              onClick={() => setSeverity('drivable')}
            />
            <Choice
              active={severity === 'not_drivable'}
              label="Nicht fahrbereit"
              onClick={() => setSeverity('not_drivable')}
            />
          </div>
          {severity === 'not_drivable' ? (
            <p className="rounded-md border border-amber-600/40 bg-amber-500/10 p-3 text-sm text-amber-900 dark:text-amber-200">
              Das Fahrzeug wird bis zur Freigabe gesperrt.
            </p>
          ) : null}
          <div className="space-y-1">
            <Label htmlFor="damage-desc">Beschreibung</Label>
            <textarea
              id="damage-desc"
              className="min-h-28 w-full rounded-md border border-input bg-background px-3 py-2 text-base"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="damage-loc">Ort (optional)</Label>
            <Input
              id="damage-loc"
              value={locationNote}
              onChange={(event) => setLocationNote(event.target.value)}
              placeholder="z. B. Parkplatz Nord"
            />
          </div>
        </div>
      ) : null}

      {step === 3 && selected ? (
        <div className={`${fleetPanelClassName} space-y-5`}>
          <p className="text-3xl font-semibold tracking-tight sm:text-4xl">
            {selected.licensePlate}
          </p>
          <p className="text-sm text-muted-foreground">
            {kind ? FLEET_DAMAGE_KIND_LABELS[kind] : ''} ·{' '}
            {severity ? FLEET_DAMAGE_SEVERITY_LABELS[severity] : ''}
          </p>
          {severity === 'not_drivable' ? (
            <p className="rounded-md border border-amber-600/40 bg-amber-500/10 p-3 text-sm text-amber-900 dark:text-amber-200">
              Das Fahrzeug wird bis zur Freigabe gesperrt.
            </p>
          ) : null}
          <div className="space-y-2">
            <Label htmlFor="damage-photos">Fotos (höchstens {FLEET_DAMAGE_PHOTO_MAX})</Label>
            <Input
              id="damage-photos"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              multiple
              onChange={onPhotosChange}
            />
            {previews.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {previews.map((src) => (
                  <img
                    key={src}
                    src={src}
                    alt=""
                    className="aspect-video w-full rounded-md object-cover"
                  />
                ))}
              </div>
            ) : null}
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
      ) : null}

      {ownVehicles.length > 0 ? (
        <div className="fixed inset-x-0 bottom-[calc(4.75rem+env(safe-area-inset-bottom))] z-30 border-t bg-background p-4 lg:static lg:bottom-auto lg:z-auto lg:border-0 lg:bg-transparent lg:p-0">
          <div className="mx-auto flex max-w-lg gap-2">
            {step > 1 ? (
              <Button
                type="button"
                variant="outline"
                className="min-h-12 flex-1"
                onClick={() => setStep((s) => (s - 1) as Step)}
              >
                Zurück
              </Button>
            ) : null}
            {step < 3 ? (
              <Button
                type="button"
                className="min-h-12 flex-1 text-base"
                disabled={
                  (step === 1 && !vehicleId) ||
                  (step === 2 && (!kind || !severity || !description.trim()))
                }
                onClick={() => setStep((s) => (s + 1) as Step)}
              >
                Weiter
              </Button>
            ) : (
              <Button
                type="button"
                className="min-h-12 flex-1 text-base"
                disabled={create.isPending}
                onClick={() => void onSubmit()}
              >
                Melden
              </Button>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Choice({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'min-h-16 rounded-xl border border-border bg-card px-3 py-3 text-base font-medium shadow-sm',
        active ? 'border-primary bg-primary/5' : 'hover:bg-muted/40',
      )}
    >
      {label}
    </button>
  );
}
